import { useCallback, useEffect, useState } from "react";
import {
  fetchOrderDetail,
  fetchOrders,
  ORDER_STAGES,
  ORDER_STATUS_FILTERS,
  type OrderDetail,
  type OrderDetailResult,
  type OrderRecord,
  type OrdersFilter,
  type OrdersListResult,
  type OrderStageName,
  type OrderStatus,
  type OrderStatusFilter,
} from "../api";
import { EMPTY_NR_CONFIG, type NrConfig } from "../newrelic";
import { OrderNrLinks } from "../NrLinks";
import { loadNrConfig, saveNrConfig } from "../storage";

// #258, ADR 0060: full order history — status/date filters (order-status-service)
// or a customer filter (switches to order-service instead, since neither
// backend can answer status+customer+date together — see the ADR), plus a
// per-order drill-down. Absorbs what load-generator-ui's "Orders" tab did
// (ADR 0045), admin-gated and over full history (no 7-day cliff — ADR 0050).

const SINCE_OPTIONS: { label: string; hours: number }[] = [
  { label: "1h", hours: 1 },
  { label: "24h", hours: 24 },
  { label: "3d", hours: 72 },
  { label: "all", hours: 0 },
];

const STATUS_LABELS: Record<OrderStatusFilter, string> = {
  all: "all",
  in_progress: "in progress",
  fulfilled: "fulfilled",
  failed: "failed",
};

export function OrdersScreen() {
  const [status, setStatus] = useState<OrderStatusFilter>("all");
  const [sinceHours, setSinceHours] = useState(24);
  const [customer, setCustomer] = useState("");
  const [list, setList] = useState<OrdersListResult | null>(null);
  const [loading, setLoading] = useState(false);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<OrderDetailResult | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [nrConfig, setNrConfig] = useState<NrConfig>(EMPTY_NR_CONFIG);
  useEffect(() => setNrConfig(loadNrConfig()), []);

  const byCustomer = customer.trim() !== "";

  const refresh = useCallback(async () => {
    setLoading(true);
    const filter: OrdersFilter = byCustomer
      ? { customer, limit: 100 }
      : { status, sinceHours, limit: 100 };
    setList(await fetchOrders(filter));
    setLoading(false);
  }, [byCustomer, customer, status, sinceHours]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function openDetail(orderId: string) {
    setSelectedId(orderId);
    setDetail(null);
    setDetailLoading(true);
    setDetail(await fetchOrderDetail(orderId));
    setDetailLoading(false);
  }

  function updateNrBaseUrl(baseUrl: string) {
    const cfg = { baseUrl };
    setNrConfig(cfg);
    saveNrConfig(cfg);
  }

  return (
    <section>
      <div className="orders-head">
        <h2>Orders</h2>
        <details className="nr-config">
          <summary>New Relic links</summary>
          <label>
            Base URL
            <input
              type="text"
              placeholder="https://one.eu.newrelic.com"
              value={nrConfig.baseUrl}
              onChange={(e) => updateNrBaseUrl(e.target.value)}
            />
          </label>
        </details>
      </div>

      <div className="orders-filters">
        <div className="status-picker" role="group" aria-label="status filter">
          {ORDER_STATUS_FILTERS.map((s) => (
            <button
              key={s}
              type="button"
              className={status === s ? "active" : ""}
              aria-pressed={status === s}
              disabled={byCustomer}
              onClick={() => setStatus(s)}
            >
              {STATUS_LABELS[s]}
            </button>
          ))}
        </div>

        <div className="since-picker">
          {SINCE_OPTIONS.map((o) => (
            <button
              key={o.label}
              type="button"
              className={sinceHours === o.hours ? "active" : ""}
              aria-pressed={sinceHours === o.hours}
              disabled={byCustomer}
              onClick={() => setSinceHours(o.hours)}
            >
              {o.label}
            </button>
          ))}
        </div>

        <input
          type="text"
          className="customer-filter"
          placeholder="customer id…"
          value={customer}
          onChange={(e) => setCustomer(e.target.value)}
        />

        <button type="button" onClick={() => void refresh()} disabled={loading}>
          {loading ? "…" : "refresh"}
        </button>
      </div>

      {byCustomer && (
        <p className="hint">
          Filtering by customer — status and date filters don't apply
          (order-service, the source for a customer's orders, doesn't carry
          fulfilment status or support a date range).
        </p>
      )}

      {list?.kind === "unavailable" && (
        <p className="hint warn">
          {list.error}
          {list.status === 503 ? " — that backend isn't deployed." : ""}
        </p>
      )}

      {list && list.kind !== "unavailable" && list.orders.length === 0 && (
        <p className="hint">No orders match these filters.</p>
      )}

      {list?.kind === "status-list" && list.orders.length > 0 && (
        <OrderStatusTable orders={list.orders} nrConfig={nrConfig} onSelect={openDetail} />
      )}

      {list?.kind === "customer-list" && list.orders.length > 0 && (
        <OrderRecordTable orders={list.orders} nrConfig={nrConfig} onSelect={openDetail} />
      )}

      {selectedId && (
        <div className="order-detail">
          <h3>
            <code>{selectedId}</code>
          </h3>
          {detailLoading && <p className="hint">Loading…</p>}
          {detail?.kind === "not_found" && <p className="hint">No such order.</p>}
          {detail?.kind === "unavailable" && <p className="hint warn">{detail.error}</p>}
          {detail?.kind === "detail" && <OrderDetailView detail={detail.body} nrConfig={nrConfig} />}
        </div>
      )}
    </section>
  );
}

function orderStatusLabel(s?: OrderStatus | null): { text: string; cls: "" | "ok" | "bad" } {
  if (!s) return { text: "unknown", cls: "" };
  if (s.status === "fulfilled") return { text: "fulfilled", cls: "ok" };
  if (s.status === "failed") {
    const stage = s.failedStage;
    const reason = stage ? s.stages[stage]?.reason : undefined;
    return { text: `failed: ${stage ?? "?"}${reason ? ` (${reason})` : ""}`, cls: "bad" };
  }
  return { text: "in progress", cls: "" };
}

/** The four `<td>` stage cells: ✓ processed / ✗ failed / · not seen. */
function StageCells({ status }: { status?: OrderStatus | null }) {
  return (
    <>
      {ORDER_STAGES.map((s: OrderStageName) => {
        const r = status?.stages[s]?.result;
        return (
          <td key={s} className={`stage ${r === "processed" ? "ok" : r === "failed" ? "bad" : "muted"}`}>
            {r === "processed" ? "✓" : r === "failed" ? "✗" : "·"}
          </td>
        );
      })}
    </>
  );
}

function fmtTime(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function OrderStatusTable({
  orders,
  nrConfig,
  onSelect,
}: {
  orders: OrderStatus[];
  nrConfig: NrConfig;
  onSelect: (orderId: string) => void;
}) {
  return (
    <table className="orders-table">
      <thead>
        <tr>
          <th>orderId</th>
          {ORDER_STAGES.map((s) => (
            <th key={s}>{s}</th>
          ))}
          <th>status</th>
          <th>updated</th>
          <th>New Relic</th>
        </tr>
      </thead>
      <tbody>
        {orders.map((o) => {
          const label = orderStatusLabel(o);
          return (
            <tr key={o.orderId} className="clickable" onClick={() => onSelect(o.orderId)}>
              <td>
                <code>{o.orderId}</code>
              </td>
              <StageCells status={o} />
              <td className={`order-status ${label.cls}`}>{label.text}</td>
              <td className="muted">{fmtTime(o.updatedAt)}</td>
              <td onClick={(e) => e.stopPropagation()}>
                <OrderNrLinks orderId={o.orderId} cfg={nrConfig} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function OrderRecordTable({
  orders,
  nrConfig,
  onSelect,
}: {
  orders: OrderRecord[];
  nrConfig: NrConfig;
  onSelect: (orderId: string) => void;
}) {
  return (
    <table className="orders-table">
      <thead>
        <tr>
          <th>orderId</th>
          <th>customer</th>
          <th>total</th>
          <th>placed</th>
          <th>New Relic</th>
        </tr>
      </thead>
      <tbody>
        {orders.map((o) => (
          <tr key={o.orderId} className="clickable" onClick={() => onSelect(o.orderId)}>
            <td>
              <code>{o.orderId}</code>
            </td>
            <td>{o.customerId}</td>
            <td>
              {o.currency} {o.total.toFixed(2)}
            </td>
            <td className="muted">{fmtTime(o.placedAt)}</td>
            <td onClick={(e) => e.stopPropagation()}>
              <OrderNrLinks orderId={o.orderId} cfg={nrConfig} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function OrderDetailView({
  detail,
  nrConfig,
}: {
  detail: OrderDetail;
  nrConfig: NrConfig;
}) {
  const { order, status } = detail;
  const label = orderStatusLabel(status);
  return (
    <>
      <p>
        {order.customerId}
        {order.customerTier ? ` (${order.customerTier})` : ""} · {order.currency}{" "}
        {order.total.toFixed(2)} · placed {fmtTime(order.placedAt)}
        {status && (
          <>
            {" "}
            · <span className={`order-status ${label.cls}`}>{label.text}</span>
          </>
        )}
      </p>

      <table className="orders-table">
        <thead>
          <tr>
            <th>sku</th>
            <th>quantity</th>
            <th>unit price</th>
          </tr>
        </thead>
        <tbody>
          {order.items.map((line) => (
            <tr key={line.sku}>
              <td>
                <code>{line.sku}</code>
              </td>
              <td>{line.quantity}</td>
              <td>
                {order.currency} {line.unitPrice.toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {status ? (
        <table className="orders-table">
          <thead>
            <tr>
              {ORDER_STAGES.map((s) => (
                <th key={s}>{s}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <StageCells status={status} />
            </tr>
          </tbody>
        </table>
      ) : (
        <p className="hint">
          No fulfilment status yet — the read model may not have caught up, or is
          disabled.
        </p>
      )}

      <p className="hint">
        <OrderNrLinks orderId={order.orderId} cfg={nrConfig} />
      </p>
    </>
  );
}
